import { Injectable, Injector } from '@angular/core';
import { WizardAbstractService } from './wizard-abstract.service';
import { WizardIdEnum } from '@app/core/model/enum/wizard-id.enum';
import { UserWizardApiService } from './user-wizard-api.service';
import { tap, catchError, map, first, timeout, take, takeUntil, filter, switchMap } from 'rxjs/operators';
import { NavigationBlockWizardService } from './navigation-block-wizard.service';
import { WizardSupportEnum } from '@app/core/model/enum/wizard-support.enum';
import { NavigationBlockWizardMobileService } from './navigation-block-wizard.service-mobile';
import { GuideWizardService } from './guide-wizard.service';
import { GuideWizardMobileService } from './guide-wizard.service-mobile';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { VignetteListWizardMobileService } from './vignette-list-wizard-mobile.service';
import { VignetteListWizardService } from './vignette-list-wizard.service';
import { DdmIndicativeWizardService } from './ddm-indicative-wizard.service';
import { CreateOrderWizardService } from './create-order-wizard.service';
import { VisibiliteStocksWizardService } from './visibilite-stocks-wizard.service';
import { AuthenticationService } from '@app/core/business/user/authentication.service';


interface WizardMetadata {
    services: Map<WizardSupportEnum, WizardAbstractService>;
    hasBeenSeen: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class WizardManagerService {
    private wizards = new Map<WizardIdEnum, WizardMetadata>();
    private initialized = false;
    private isShowingWizard = false;
    private backendLoaded$ = new ReplaySubject<void>();

    constructor(
        private injector: Injector,
        private userWizardApi: UserWizardApiService,
        private authenticationService: AuthenticationService
    ) {
        this.initialize();
        this.authenticationService.isConnected$.pipe(
            filter(isConnected => !!isConnected),
            switchMap(() => this.loadSeenWizardsFromBackend())
        ).subscribe();
    }

    /**
        * Initialise tous les wizards (à appeler une seule fois)
        */
    private initialize(): void {
        if (this.initialized) {
            return;
        }

        // Injecter et enregistrer tous les wizards ici
        // this.register(WizardIdEnum.CREATE_ORDER_WIZARD, new Map([
        //     [WizardSupportEnum.DESKTOP, this.injector.get(CreateOrderWizardService)],
        //     [WizardSupportEnum.TABLET, this.injector.get(CreateOrderWizardService)],
        //     [WizardSupportEnum.MOBILE, this.injector.get(CreateOrderWizardService)],
        // ]));


        this.register(WizardIdEnum.NAVIGATION_BLOCK, new Map<WizardSupportEnum, WizardAbstractService>([
            [WizardSupportEnum.DESKTOP, this.injector.get(NavigationBlockWizardService)],
            [WizardSupportEnum.TABLET, this.injector.get(NavigationBlockWizardService)],
            [WizardSupportEnum.MOBILE, this.injector.get(NavigationBlockWizardMobileService)],
        ]));

        // this.register(WizardIdEnum.GUIDE_PRESENTATION, new Map<WizardSupportEnum, WizardAbstractService>([
        //     [WizardSupportEnum.DESKTOP, this.injector.get(GuideWizardService)],
        //     [WizardSupportEnum.TABLET, this.injector.get(GuideWizardService)],
        //     [WizardSupportEnum.MOBILE, this.injector.get(GuideWizardMobileService)],
        // ]));



        this.register(WizardIdEnum.VIGNETTE_LIST_PRESENTATION, new Map<WizardSupportEnum, WizardAbstractService>([
            [WizardSupportEnum.DESKTOP, this.injector.get(VignetteListWizardService)],
            [WizardSupportEnum.TABLET, this.injector.get(VignetteListWizardService)],
            [WizardSupportEnum.MOBILE, this.injector.get(VignetteListWizardMobileService)],
        ]));

        this.register(WizardIdEnum.DDM_INDICATIVE, new Map<WizardSupportEnum, WizardAbstractService>([
            [WizardSupportEnum.DESKTOP, this.injector.get(DdmIndicativeWizardService)],
            [WizardSupportEnum.TABLET, this.injector.get(DdmIndicativeWizardService)],
            [WizardSupportEnum.MOBILE, this.injector.get(DdmIndicativeWizardService)],
        ]));

        this.register(WizardIdEnum.VISIBILITE_STOCKS, new Map<WizardSupportEnum, WizardAbstractService>([
            [WizardSupportEnum.DESKTOP, this.injector.get(VisibiliteStocksWizardService)],
            [WizardSupportEnum.TABLET, this.injector.get(VisibiliteStocksWizardService)],
            [WizardSupportEnum.MOBILE, this.injector.get(VisibiliteStocksWizardService)],
        ]));

        this.initialized = true;
    }

    /**
     *  Détecte le support actuel
     */
    private detectSupport(): WizardSupportEnum {
        const width = window.innerWidth;
        if (width < 768) return WizardSupportEnum.MOBILE;
        return WizardSupportEnum.DESKTOP;
    }



    getWizardInitRoute(wizardId: WizardIdEnum): string {
        const service = this.getServiceForCurrentSupport(wizardId);
        return service ? service.getInitRoute() : '/dashboard';
    }

    /**
    *  Récupère le service pour le support actuel
    */
    private getServiceForCurrentSupport(wizardId: WizardIdEnum): WizardAbstractService | null {
        const wizard = this.wizards.get(wizardId);
        if (!wizard) return null;

        // Essayer le support actuel
        let service = wizard.services.get(this.detectSupport());

        // Fallback vers desktop si pas de version pour ce support
        if (!service && this.detectSupport() !== WizardSupportEnum.DESKTOP) {
            service = wizard.services.get(WizardSupportEnum.DESKTOP);
        }

        return service || null;
    }


    /**
     * Enregistre un wizard avec un dictionnaire de supports
     */
    register(
        wizardId: WizardIdEnum,
        supportServices: Map<WizardSupportEnum, WizardAbstractService>
    ): void {
        this.wizards.set(wizardId, {
            services: supportServices,
            hasBeenSeen: false,
        });
    }


    /**
     * Charge les wizards vus depuis le backend
     */
    loadSeenWizardsFromBackend(): Observable<void> {
        return this.userWizardApi.getUserWizards().pipe(
            tap(seenWizardIds => {
                seenWizardIds.forEach(wizardId => {
                    const wizard = this.wizards.get(wizardId as WizardIdEnum);
                    if (wizard) {
                        wizard.hasBeenSeen = true;
                    }
                });
                this.backendLoaded$.next();
                this.backendLoaded$.complete();
            }),
            catchError(error => {
                console.error('Error loading wizards from backend:', error);
                this.backendLoaded$.next();
                this.backendLoaded$.complete();
                return of(void 0);
            })
        );
    }

    /**
     *  Attend que le backend soit chargé (avec timeout de 4s)
     */
    private waitForBackendLoad$(): Observable<void> {
        return this.backendLoaded$.pipe(
            first(),
            timeout(4000),
            catchError((err) => {
                if (err && err.name == 'TimeoutError')
                    console.warn('⏱️ Backend load timeout (4s) - proceeding anyway');
                throw err;
            })
        );
    }


    /**
     * Démarre un wizard par son ID, ou le premier disponible et non vu parmi une liste ordonnée par priorité.
     */
    start(wizardId: WizardIdEnum, force?: boolean): void;
    start(wizardIds: WizardIdEnum[], force?: boolean): void;
    start(wizardIdOrIds: WizardIdEnum | WizardIdEnum[], force: boolean = false): void {
        const ids = Array.isArray(wizardIdOrIds) ? wizardIdOrIds : [wizardIdOrIds];
        this.waitForBackendLoad$().subscribe({
            next: () => {
                for (const id of ids) {
                    if (this.startWizardInternal(id, force)) {
                        break;
                    }
                }
            },
            error: (err) => console.error('Failed to start wizard:', err)
        });
    }

    /**
     * Démarre le premier wizard disponible et non vu parmi une liste ordonnée par priorité.
     */
    startFirstAvailable(wizardIds: WizardIdEnum[], force: boolean = false): void {
        this.start(wizardIds, force);
    }

    /**
     * Démarre un wizard par son ID
     */
    startWizardInternal(wizardId: WizardIdEnum, force: boolean = false): boolean {
        const wizard = this.wizards.get(wizardId);

        if (!wizard) {
            console.warn(`Wizard "${wizardId}" not found. Did you forget to register it?`);
            return false;
        }

        // Si déjà vu et pas forcé, ne rien faire
        if (wizard.hasBeenSeen && !force) {
            return false;
        }

        const service = this.getServiceForCurrentSupport(wizardId);
        if (!service) {
            console.warn(`No service found for wizard "${wizardId}"`);
            return false;
        }

        if (!service.isAvailable()) {
            return false;
        }

        if (!this.isShowingWizard) {
            this.isShowingWizard = true;

            service.onWizardEnd$.pipe(
                take(1),
                tap(() => {
                    this.isShowingWizard = false;
                    wizard.hasBeenSeen = true;

                    // Sauvegarder en backend
                    this.userWizardApi.markWizardAsSeen(wizardId).subscribe({
                        error: (err) => console.error(`Error marking wizard "${wizardId}" as seen:`, err)
                    });
                })
            ).subscribe();

            service.start();
            return true;
        }

        return false;
    }

    /**
    * Démarre un wizard avec un délai
    */
    startWithDelay(wizardId: WizardIdEnum, delayMs: number = 500, force: boolean = false): void {
        setTimeout(() => {
            this.start(wizardId, force);
        }, delayMs);
    }

    /**
     * Vérifie si un wizard a déjà été vu (dans cette session)
     */
    hasBeenSeen(wizardId: WizardIdEnum): boolean {
        const wizard = this.wizards.get(wizardId);
        return wizard?.hasBeenSeen ?? false;
    }


    /**
     * Marque un wizard comme vu (utile pour l'intégration BDD future)
     */
    markAsSeen(wizardId: WizardIdEnum): Observable<boolean> {
        const wizard = this.wizards.get(wizardId);

        if (!wizard) {
            console.warn(`Wizard "${wizardId}" not found`);
            return of(false);
        }

        wizard.hasBeenSeen = true;
        // Sauvegarder en backend
        return this.userWizardApi.markWizardAsSeen(wizardId)
            .pipe(map(() => true),
                catchError(err => {
                    console.error(`Error marking wizard "${wizardId}" as seen:`, err);
                    throw err;
                })
            );
    }

    /**
    * Marque un wizard comme non-vu
    */
    markAsUnseen(wizardId: WizardIdEnum): Observable<boolean> {
        const wizard = this.wizards.get(wizardId);

        if (!wizard) {
            console.warn(`Wizard "${wizardId}" not found`);
            return;
        }

        wizard.hasBeenSeen = false;
        return this.userWizardApi.deleteUserWizard(wizardId).pipe(
            map(() => true),
            catchError(err => {
                console.error(`Error marking wizard "${wizardId}" as unseen:`, err);
                throw err;
            })
        );
    }


    /**
    * Liste tous les wizards enregistrés et disponibles pour le contexte utilisateur
    */
    listWizards(): Array<{ id: WizardIdEnum; hasBeenSeen: boolean; }> {
        return Array.from(this.wizards.entries())
            .filter(([id, _]) => {
                const service = this.getServiceForCurrentSupport(id);
                return service ? service.isAvailable() : false;
            })
            .map(([id, wizard]) => ({
                id,
                hasBeenSeen: wizard.hasBeenSeen,
            }));
    }

    /**
     * récupère un wizard(le service) par son ID
     * @param wizardId 
     * @returns 
     */
    getWizard(wizardId: WizardIdEnum): WizardAbstractService {
        return this.getServiceForCurrentSupport(wizardId);
    }

    /**
     * Récupère tous les IDs de wizards enregistrés
     */
    getAllWizardIds(): string[] {
        return Array.from(this.wizards.keys());
    }

    resetAllSeenFlags(): void {
        this.wizards.forEach((wizard, id) => {
            wizard.hasBeenSeen = false;
        });

        // Supprimer tous du backend
        this.userWizardApi.deleteAllUserWizards().subscribe({
            error: (err) => console.error('Error resetting all wizards:', err)
        });
    }

}